/**
 * Addressbook Redux Slice
 */

import { createSlice, type PayloadAction } from '@ufc/core';
import type {
  Address,
  AddressStats,
  AddressesFilter,
  CreateAddressRequest,
  UpdateAddressRequest,
  LocationType,
  Country,
  City,
  Region,
  PaginationMeta,
  PaginatedAddresses,
} from '../domain/types';
import { DEFAULT_PAGE_SIZE } from '../domain/constants';

export interface AddressbookState {
  items: Address[];
  selectedAddress: Address | null;
  stats: AddressStats;
  locationTypes: LocationType[];
  countries: Country[];
  cities: City[];
  regions: Region[];
  filter: AddressesFilter;
  pagination: PaginationMeta;
  /** Loading state for list/fetch operations */
  loading: boolean;
  /** Submitting state for create/update/delete operations */
  isSubmitting: boolean;
  /** Error message from last failed operation */
  error: string | null;
}

const initialState: AddressbookState = {
  items: [],
  selectedAddress: null,
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
    byType: {
      site: 0,
      office: 0,
      warehouse: 0,
      home: 0,
      plant: 0,
      customer: 0,
    },
  },
  locationTypes: [],
  countries: [],
  cities: [],
  regions: [],
  filter: { type: 'all', page: 1, pageSize: DEFAULT_PAGE_SIZE },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE, pageCount: 1, total: 0 },
  loading: false,
  isSubmitting: false,
  error: null,
};

export const addressbookSlice = createSlice({
  name: 'addressbook',
  initialState,
  reducers: {
    // Fetch actions
    fetchAddresses: (
      state,
      action: PayloadAction<AddressesFilter | undefined>,
    ) => {
      state.loading = true;
      state.error = null;
      if (action.payload) {
        state.filter = { ...state.filter, ...action.payload };
      }
    },
    fetchAddressesSuccess: (
      state,
      action: PayloadAction<PaginatedAddresses>,
    ) => {
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
      state.loading = false;
      state.error = null;
    },
    fetchAddressesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch single address
    fetchAddress: (state, _action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchAddressSuccess: (state, action: PayloadAction<Address>) => {
      state.selectedAddress = action.payload;
      state.loading = false;
    },
    fetchAddressFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Stats actions
    fetchStats: (state) => {
      state.loading = true;
    },
    fetchStatsSuccess: (state, action: PayloadAction<AddressStats>) => {
      state.stats = action.payload;
      state.loading = false;
    },
    fetchStatsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Location types actions
    fetchLocationTypes: (state) => {
      state.loading = true;
    },
    fetchLocationTypesSuccess: (
      state,
      action: PayloadAction<LocationType[]>,
    ) => {
      state.locationTypes = action.payload;
      state.loading = false;
    },
    fetchLocationTypesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Countries actions
    fetchCountries: (state) => {
      state.loading = true;
    },
    fetchCountriesSuccess: (state, action: PayloadAction<Country[]>) => {
      state.countries = action.payload;
      state.loading = false;
    },
    fetchCountriesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Cities actions
    fetchCities: (state, _action: PayloadAction<string | undefined>) => {
      state.loading = true;
    },
    fetchCitiesSuccess: (state, action: PayloadAction<City[]>) => {
      state.cities = action.payload;
      state.loading = false;
    },
    fetchCitiesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Regions actions
    fetchRegions: (state) => {
      state.loading = true;
    },
    fetchRegionsSuccess: (state, action: PayloadAction<Region[]>) => {
      state.regions = action.payload;
      state.loading = false;
    },
    fetchRegionsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create actions
    createAddress: (state, _action: PayloadAction<CreateAddressRequest>) => {
      state.isSubmitting = true;
      state.error = null;
    },
    createAddressSuccess: (state, action: PayloadAction<Address>) => {
      state.items = [...state.items, action.payload];
      state.isSubmitting = false;
      state.error = null;
    },
    createAddressFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false;
      state.error = action.payload;
    },

    // Update actions
    updateAddress: (
      state,
      _action: PayloadAction<{
        id: string;
        documentId: string;
        data: UpdateAddressRequest;
      }>,
    ) => {
      state.isSubmitting = true;
      state.error = null;
    },
    updateAddressSuccess: (state, action: PayloadAction<Address>) => {
      state.items = state.items.map((addr) =>
        addr.id === action.payload.id ? action.payload : addr,
      );
      if (state.selectedAddress?.id === action.payload.id) {
        state.selectedAddress = action.payload;
      }
      state.isSubmitting = false;
      state.error = null;
    },
    updateAddressFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false;
      state.error = action.payload;
    },

    // Delete actions
    deleteAddress: (
      state,
      _action: PayloadAction<{ id: string; documentId: string }>,
    ) => {
      state.isSubmitting = true;
      state.error = null;
    },
    deleteAddressSuccess: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((addr) => addr.id !== action.payload);
      if (state.selectedAddress?.id === action.payload) {
        state.selectedAddress = null;
      }
      state.isSubmitting = false;
      state.error = null;
    },
    deleteAddressFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false;
      state.error = action.payload;
    },

    // Toggle favorite
    toggleFavorite: (
      state,
      _action: PayloadAction<{ id: string; documentId: string }>,
    ) => {
      state.loading = true;
    },
    toggleFavoriteSuccess: (state, action: PayloadAction<Address>) => {
      state.items = state.items.map((addr) =>
        addr.id === action.payload.id ? action.payload : addr,
      );
      state.loading = false;
    },
    toggleFavoriteFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Set default
    setDefault: (
      state,
      _action: PayloadAction<{ id: string; documentId: string }>,
    ) => {
      state.loading = true;
    },
    setDefaultSuccess: (state, action: PayloadAction<Address>) => {
      // Unset previous defaults
      state.items = state.items.map((addr) => ({
        ...addr,
        isDefault: addr.id === action.payload.id,
      }));
      state.loading = false;
    },
    setDefaultFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // State setters (for direct state manipulation)
    setAddresses: (state, action: PayloadAction<Address[]>) => {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    setSelectedAddress: (state, action: PayloadAction<Address | null>) => {
      state.selectedAddress = action.payload;
    },
    setStats: (state, action: PayloadAction<AddressStats>) => {
      state.stats = action.payload;
    },
    setFilter: (state, action: PayloadAction<AddressesFilter>) => {
      state.filter = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Search and filter actions
    searchAddresses: (state, action: PayloadAction<string>) => {
      state.filter = { ...state.filter, search: action.payload };
    },
    filterByType: (state, action: PayloadAction<AddressesFilter['type']>) => {
      state.filter = {
        ...state.filter,
        type: action.payload,
        locationTypeCode: undefined,
        page: 1,
      };
    },
    filterByStatus: (
      state,
      action: PayloadAction<AddressesFilter['status']>,
    ) => {
      state.filter = { ...state.filter, status: action.payload, page: 1 };
    },
    filterByCityId: (state, action: PayloadAction<string | undefined>) => {
      state.filter = { ...state.filter, cityId: action.payload, page: 1 };
    },
    filterByCountryCode: (state, action: PayloadAction<string | undefined>) => {
      state.filter = { ...state.filter, countryCode: action.payload, page: 1 };
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, page: action.payload };
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, pageSize: action.payload, page: 1 };
    },
    goToNextPage: (state) => {
      const currentPage = state.filter.page ?? 1;
      if (currentPage < state.pagination.pageCount) {
        state.filter = { ...state.filter, page: currentPage + 1 };
      }
    },
    goToPreviousPage: (state) => {
      const currentPage = state.filter.page ?? 1;
      if (currentPage > 1) {
        state.filter = { ...state.filter, page: currentPage - 1 };
      }
    },

    // Clear state
    clearAddresses: (state) => {
      state.items = [];
      state.selectedAddress = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const addressbookActions = addressbookSlice.actions;
export const addressbookReducer = addressbookSlice.reducer;
